/* ──────────────────────────── ExtractorsTable.js ──────────────────────────── */
import React, { useContext, useEffect, useState } from "react";
import {
  ProjectContext,
  PdfHighlightContext,
} from "../../context/ProjectProvider";
import {
  createRowAPI,
  updateRowAPI,
  deleteRowAPI,
  sendExtractionFeedbackAPI,
} from "../../utils/api";
import HeaderExtract from "./HeaderExtract";
import HeaderTable   from "./HeaderTable";
import Feedback from "./Helpers/Feedback"; 
import "./ExtractorsTable.css";

// --- solo shift+enter (newline) separa keywords
const parseKeywords = (str = "") =>
  str
    .split(/\n/)
    .filter(s => s.length > 0); // sin trim, guarda espacios tal como están



const uiToApiRow = r => ({
  group_name: r.groupName.trim(),
  criteria  : {
    string_match_keywords : parseKeywords(r.stringKeywords),
    vector_match_keywords : parseKeywords(r.vectorKeywords),
    summary_instructions  : r.summaryInstructions ?? ""
  },
});

const apiToUiRow = r => ({
  id            : r.id,
  groupName     : r.groupName ?? r.group_name ?? "",
  stringKeywords: Array.isArray(r.criteria?.string_match_keywords)
    ? r.criteria.string_match_keywords.join("\n")  // ← cada keyword en su línea
    : "",
  vectorKeywords: Array.isArray(r.criteria?.vector_match_keywords)
    ? r.criteria.vector_match_keywords.join("\n")  // ← idem
    : "",
  summaryInstructions: r.criteria?.summary_instructions ?? "",
  extraction       : r.summary ?? "",
  chunk_coordinates: r.chunk_coordinates ?? {},
});

/* ╰────────────────────────────────────────────────────────────────────╯ */

function KeywordPreview({ value }) {
  const keywords = value.split("\n").filter(Boolean);
  return (
    <div className="keyword-preview">
      {keywords.map((kw, i) => (
        <div key={i} className="keyword-item">
          {kw}
          {i < keywords.length - 1 && <div className="keyword-divider" />}
        </div>
      ))}
    </div>
  );
}

export default function ExtractorsTable() {
  const {
    tables,
    setTables,
    activeTableId,
    selectedProject,
    confirm,
  } = useContext(ProjectContext);

  const { goTo } = useContext(PdfHighlightContext);

  /* ------------------------------ estado filas ------------------------------- */
  const [rowsByTable, setRowsByTable] = useState({});
  const [isAdding, setIsAdding]       = useState(false);
  const [loadingRows, setLoadingRows] = useState({});
  const [openFeedbackRow, setOpenFeedbackRow] = useState(null); // fila con feedback abierto
  const [thanksRow, setThanksRow] = useState(null);             // fila que muestra “Thanks!”
  const [editingCell, setEditingCell] = useState({ rowId: null, field: null });

  /* carga inicial de filas al cambiar de tabla */
  useEffect(() => {
    if (!activeTableId) return;
    const table = tables.find(t => t.id === activeTableId);
    if (!table) return;

    const initial = (table.rows || []).map(r =>
      r.criteria ? apiToUiRow(r) : r
    );
    setRowsByTable(p => ({ ...p, [activeTableId]: initial }));
  }, [activeTableId, tables]);

  const getRows = () => rowsByTable[activeTableId] || [];

  /* ─── render de la columna Extraction con referencias clicables ─── */
  const renderSummary = (row) => {
    if (!row.extraction) return row.extraction;

    return row.extraction
      .split(/(\[\[[^\]]+\]\])/g)
      .map((part, i) => {
        const m = part.match(/^\[\[([^\]]+)\]\]$/);
        if (!m) return part;

        const nums = m[1].split(/\s*,\s*/);

        return (
          <React.Fragment key={i}>
            [
            {nums.map((n, idx) => {
              const chunk = row.chunk_coordinates?.[n];
              if (!chunk) {
                return (
                  <React.Fragment key={n}>
                    {n}{idx < nums.length - 1 ? ", " : ""}
                  </React.Fragment>
                );
              }

                            /* -----------------------------------------------------------------
                               Ahora obtenemos la página directamente del atributo `page`
                               que trae cada palabra y agrupamos POR LÍNEA Y POR PÁGINA.
                            ----------------------------------------------------------------- */
                            const groupByLine = (words) => {
                              const groups = [];
                              words.forEach(w => {
                                const g = groups.find(
                                  grp => grp.page === w.page && Math.abs(grp.top - w.top) <= 1
                                );
                                if (g) {
                                  g.words.push(w);
                                  g.x0 = Math.min(g.x0, w.x0);
                                  g.x1 = Math.max(g.x1, w.x1);
                                } else {
                                  groups.push({
                                    page  : w.page,
                                    top   : w.top,
                                    bottom: w.bottom,
                                    x0    : w.x0,
                                    x1    : w.x1,
                                    words : [w],
                                  });
                                }
                              });
                              return groups;
                            };
              
                            const rects = [];
                            groupByLine(chunk.context_word_coords ?? []).forEach(g => {
                              rects.push({ ...g, kind: "context" });
                            });
                            groupByLine(chunk.center_word_coords ?? []).forEach(g => {
                              rects.push({ ...g, kind: "center"  });
                            });
              
                            /* Página a la que haremos scroll: la primera de las referencias */
                            const scrollPage =
                              rects.length ? Math.min(...rects.map(r => r.page ?? 1)) : 1;

              return (
                <React.Fragment key={n}>
                  <button
                    className="ref-btn"
                    onClick={() => goTo(scrollPage, rects)}
                    title={`Go to page ${scrollPage}`}
                  >
                    {n}
                  </button>
                  {idx < nums.length - 1 ? ", " : ""}
                </React.Fragment>
              );
            })}
            ]
          </React.Fragment>
        );
      });
  };

  /* ---------------------------- helpers CRUD filas --------------------------- */
  const activeTable = tables.find(t => t.id === activeTableId);
  const isLocked    = activeTable?.extractionStatus === "in-progress";

    /* ─── helpers feedback ──────────────────────────────────────────────── */
  const showThanks = (rowId) => {
    setThanksRow(rowId);
    setTimeout(() => setThanksRow(null), 3000); // 3 s
  };

  const handleVote = (rowId, vote, text) => {
    /* 💡  Aquí puedes enviar el voto al backend si lo necesitas */
        (async () => {
            try {
              await sendExtractionFeedbackAPI({
                extraction_row_id     : rowId,
                user_feedback_comment : text,
                is_liked              : vote === "up",
                is_disliked           : vote === "down",
                not_like_not_dislike  : vote === "neutral",
              });
            } catch (err) {
              // eslint-disable-next-line no-alert
              window.alert(err.message);
            } finally {
              setOpenFeedbackRow(null);
              showThanks(rowId);
            }
          })();
           };

  const addRow = async () => {
    if (isLocked || !selectedProject || !activeTableId || isAdding) return;
    setIsAdding(true);
    try {
      const apiRow  = await createRowAPI(
        selectedProject.id,
        activeTableId,
        { group_name: "", criteria: { string_match_keywords: [], vector_match_keywords: [], summary_instructions: "" } }
      );
      const newRow  = apiToUiRow(apiRow);
      const updated = [...getRows(), newRow];
      setRowsByTable(p => ({ ...p, [activeTableId]: updated }));
      setTables(p => p.map(t => t.id === activeTableId ? { ...t, rows: updated } : t));
    } catch (err) {
      console.error(err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleCellChange = (rowId, field, value) => {
    const updated = getRows().map(r =>
      r.id === rowId ? { ...r, [field]: value } : r
    );
    setRowsByTable(p => ({ ...p, [activeTableId]: updated }));
  };

const handleCellBlur = async (rowId, field, event) => {
  if (isLocked || !selectedProject) return;

  const row = getRows().find(r => r.id === rowId);

  if (field === "stringKeywords") {
    const keywords = parseKeywords(row.stringKeywords);
    const tooShort = keywords.find(k => k.length < 4);

    if (tooShort) {
      window.alert(`Each keyword must be at least 4 characters long (including spaces). Problematic keyword: "${tooShort}"`);
      
      // Reactiva la celda
      setTimeout(() => {
        setEditingCell({ rowId, field });
      }, 0);

      return;
    }
  }

  setLoadingRows(p => ({ ...p, [rowId]: true }));
  try {
    const apiRow = await updateRowAPI(
      selectedProject.id,
      activeTableId,
      rowId,
      uiToApiRow(row)
    );
    const synced     = apiToUiRow(apiRow);
    const syncedRows = getRows().map(r => r.id === rowId ? synced : r);
    setRowsByTable(p => ({ ...p, [activeTableId]: syncedRows }));
    setTables(p => p.map(t =>
      t.id === activeTableId ? { ...t, rows: syncedRows } : t
    ));
    // Solo cerramos la edición si todo fue bien
    setEditingCell({ rowId: null, field: null });
  } catch (err) {
    console.error(err);
  } finally {
    setLoadingRows(p => ({ ...p, [rowId]: false }));
  }
};


  const removeRow = async (rowId) => {
    if (isLocked || !selectedProject) return;
  
    const confirmed = await confirm({
      title: "Delete row?",
      message: "Are you sure you want to delete this row? This action cannot be undone.",
    });
  
    if (!confirmed) return;
  
    setLoadingRows(p => ({ ...p, [rowId]: true }));
    try {
      await deleteRowAPI(selectedProject.id, activeTableId, rowId);
      const updated = getRows().filter(r => r.id !== rowId);
      setRowsByTable(p => ({ ...p, [activeTableId]: updated }));
      setTables(p => p.map(t =>
        t.id === activeTableId ? { ...t, rows: updated } : t
      ));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRows(p => ({ ...p, [rowId]: false }));
    }
  };  

  /* ------------------------------------------------------------------------- */
  return (
  <div className="group-container">
    <HeaderExtract />
    <HeaderTable />

    {activeTable && (
      <div
        className="extractor-content-scrollable"
        style={{ opacity: isLocked ? 0.6 : 1 }}
      >
        <table className="extract-table" style={{ width: "100%" }}>
          <thead>
            <tr className="extract-table-header">
              <th>Group Name</th>
              <th>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 2,
                      backgroundColor: "rgba(0,180,0,.7)",
                      display: "inline-block"
                    }}
                  />
                  String Keywords
                </span>
              </th>
              <th>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 2,
                      backgroundColor: "rgba(255,180,0,.7)",
                      display: "inline-block"
                    }}
                  />
                  Vector Keywords
                </span>
              </th>
              <th>Summary instructions</th>
              <th>Extraction</th>
              <th /> {/* feedback */}
              <th /> {/* delete */}
            </tr>
          </thead>
          <tbody>
            {getRows().map(row => (
              <tr key={row.id}>
                {/* Group Name */}
                <td>
                  <input
                    type="text"
                    value={row.groupName}
                    disabled={isLocked || !!loadingRows[row.id]}
                    onChange={e => handleCellChange(row.id, "groupName", e.target.value)}
                    onBlur={e => handleCellBlur(row.id, "groupName", e)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
                    style={{ width: "100%" }}
                  />
                </td>

                {/* String & Vector Keywords */}
                {['stringKeywords', 'vectorKeywords'].map(f => (
                  <td key={f} className="kw-cell">
                    {editingCell.rowId === row.id && editingCell.field === f ? (
<textarea
  ref={el => {
    if (!el) return;
    // solo la primera vez que se monta
    if (!el.dataset.focused) {
      el.dataset.focused = "1";
      el.focus();               // damos foco pero NO movemos el caret
      /* ⬆️ eliminado: el.setSelectionRange(...) */
    }
  }}
  value={row[f]}
  onChange={e => handleCellChange(row.id, f, e.target.value)}
  onBlur={e => handleCellBlur(row.id, f, e)}
  onKeyDown={e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.target.blur();
    }
  }}
  rows={Math.max(1, row[f].split("\n").length)}
/>
                    ) : (
                      <div
                        className="keyword-preview-container"
                        onClick={() => setEditingCell({ rowId: row.id, field: f })}
                      >
                        <KeywordPreview value={row[f]} />
                      </div>
                    )}
                  </td>
                ))}

                {/* Summary instructions */}
                <td className="summary-cell">
                  <textarea
                    value={row.summaryInstructions}
                    disabled={isLocked || !!loadingRows[row.id]}
                    onChange={e => handleCellChange(row.id, "summaryInstructions", e.target.value)}
                    onBlur={e => handleCellBlur(row.id, "summaryInstructions", e)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.target.blur();
                      }
                    }}
                    rows={Math.max(1, (row.summaryInstructions || "").split("\n").length)}
                  />
                </td>

                {/* Extraction (clickable refs) */}
                <td>{renderSummary(row)}</td>

                {/* Feedback */}
                <td style={{ width: 32, textAlign: "center", position: "relative" }}>
                  {openFeedbackRow === row.id ? (
                    <Feedback
                      onClose={() => setOpenFeedbackRow(null)}
                      onVote={(vote, text) => handleVote(row.id, vote, text)}
                    />
                  ) : (
                    <button
                      onClick={() => setOpenFeedbackRow(row.id)}
                      disabled={isLocked}
                      className={`icon-button feedback-btn${thanksRow === row.id ? " thanks" : ""}`}
                      title="Feedback"
                    >
                      {thanksRow === row.id ? (
                        "Thanks!"
                      ) : (
                        <i className="bi bi-layout-sidebar-inset-reverse"></i>
                      )}
                    </button>
                  )}
                </td>

                {/* Delete */}
                <td style={{ width: 32, textAlign: "center" }}>
                  {loadingRows[row.id] ? (
                    <div className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <button
                      onClick={() => removeRow(row.id)}
                      disabled={isLocked}
                      className="delete-row-btn"
                      title="Delete row"
                    >
                      <i className="bi bi-trash3"></i>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 8 }}>
          <button
            onClick={addRow}
            className="add-row-btn icon-button"
            disabled={isAdding || isLocked}
          >
            {isAdding ? (
              <div className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <>
                <i className="bi bi-plus"></i>
                <span className="button-tooltip">Add row</span>
              </>
            )}
          </button>
        </div>
      </div>
    )}
  </div>
);
}
